import React from 'react';
import { revWindow, RevString } from '@revdotcom/common';
import { Mp3Helper } from './Mp3Helper';
import { DownloadTrackerHelper } from './DownloadTrackerHelper';
import { ToolState } from './AudioTrimmerCutterModels';
import { PreviewAudio } from './PreviewAudio';
import { SignUpGate } from './SignUpGate';
/**
 * An Audio Trimmer & Cutter component that allows the user to upload, preview, then trim a file.
 * The user can then add the trimmed file to their TC cart and/or download it.
 */
export class AudioTrimmerCutter extends React.Component {
    constructor(props) {
        super(props);
        this.initAudioContext = () => {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        };
        this.disconnectContext = () => {
            if (this.audioContext && this.audioContext.state !== 'closed') {
                this.audioContext.close();
            }
        };
        this.initMp3Helper = (numberOfChannels) => {
            // Using the audio context's sample rate to assure it matches
            // the final sample rate used by wavesurfer
            this.mp3Helper = new Mp3Helper({
                channelCount: numberOfChannels,
                sampleRate: this.audioContext.sampleRate,
                bitRate: 64 * numberOfChannels,
            });
        };
        this.handleFileInputChange = async (event) => {
            const file = event.target.files[0];
            if (file) {
                if (file.size > AudioTrimmerCutter.MaxFileSize) {
                    this.setState({
                        toolState: ToolState.Ready,
                        urlDownloadLink: '',
                        isFileTooLarge: true,
                    });
                }
                else {
                    const reader = new FileReader();
                    reader.onload = async (progressEvent) => {
                        // User has uploaded a file, so warn user of unsaved changes before leaving the page
                        this.addUnloadWarning();
                        this.fileName = this.getNormalizedExtensionlessFileName(file.name);
                        const fileReader = progressEvent.target;
                        const arrayBuffer = fileReader.result;
                        // Audio context detaches the buffer to decode, so make a copy before getting channels
                        await this.audioContext.decodeAudioData(arrayBuffer.slice(0)).then((data) => {
                            this.setUpForPreviewing(data.numberOfChannels, arrayBuffer);
                        });
                    };
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    reader.onerror = (_) => {
                        this.fileName = null;
                        this.setState({
                            toolState: ToolState.Ready,
                            urlDownloadLink: '',
                            isFileTooLarge: false,
                        });
                    };
                    reader.readAsArrayBuffer(file);
                }
            }
        };
        this.getNormalizedExtensionlessFileName = (fileName) => {
            const extensionlessFileName = this.getExtensionlessFileName(fileName);
            let strippedFileName = RevString.stripBidirectionalCharacters(extensionlessFileName).normalize('NFC');
            if (!strippedFileName) {
                strippedFileName = 'Audio';
            }
            return strippedFileName;
        };
        this.setUpForPreviewing = (numberOfChannels, arrayBuffer) => {
            this.initMp3Helper(numberOfChannels);
            const url = this.mp3Helper.generateDownloadableUrl([new Uint8Array(arrayBuffer)]);
            this.setState({
                toolState: ToolState.Previewing,
                urlDownloadLink: url,
                isFileTooLarge: false,
            });
        };
        this.getExtensionlessFileName = (fileName) => {
            return fileName.indexOf('.') > -1 ? fileName.substr(0, fileName.lastIndexOf('.')) : fileName;
        };
        this.addUnloadWarning = () => {
            window.addEventListener('beforeunload', this.warnBeforeUnload);
        };
        this.removeUnloadWarning = () => {
            window.removeEventListener('beforeunload', this.warnBeforeUnload);
        };
        this.warnBeforeUnload = (event) => {
            // Standard states to prevent default for beforeunload events
            event.preventDefault();
            event.returnValue = 'Changes you made may not be saved.';
        };
        this.sendUploadSegmentEvent = () => {
            window.analytics.track('Button Click', {
                altEventName: 'Audio Trimmer Cutter: Upload',
                stateChange: 'File Explorer Open',
                locationUrl: revWindow.baseUrl(),
                buttonText: 'Upload',
                buttonLocation: 'Main Page',
            });
        };
        this.warnBeforeDeleting = () => {
            this.setState({
                toolState: ToolState.Deleting,
            });
        };
        this.deleteRecording = () => {
            window.analytics.track('Button Click', {
                altEventName: 'Audio Trimmer Cutter: Delete',
                stateChange: 'Redirectless page change',
                locationUrl: revWindow.baseUrl(),
                buttonText: 'Delete',
                buttonLocation: 'Main Page (after file uploaded)',
            });
            this.removeUnloadWarning();
            this.fileName = null;
            this.setState({
                toolState: ToolState.Ready,
                urlDownloadLink: '',
            });
        };
        this.returnToPreview = () => {
            this.setState({
                toolState: ToolState.Previewing,
            });
        };
        this.handleTranscribeComplete = () => {
            this.removeUnloadWarning();
            this.fileName = null;
            this.setState({
                toolState: ToolState.Ready,
                urlDownloadLink: '',
                isFileTooLarge: false,
            });
        };
        this.onDownloadClick = () => {
            DownloadTrackerHelper.handleDownloadClick(AudioTrimmerCutter.DownloadTrackerKey, this.props.isAuthenticated);
        };
        this.renderSignUpGateState = () => {
            return SignUpGate({
                toolName: 'Audio Trimmer',
                returnUrl: '/audiotrimmercutter',
            });
        };
        this.renderReadyState = () => {
            return (React.createElement("div", { className: "card pv3" },
                React.createElement("div", { id: "trimmer-ready-img", className: "mv1 bg-center" }),
                React.createElement("div", { className: "ready-text" }, "Upload your file to start trimming"),
                React.createElement("label", { htmlFor: "file-input", id: "upload-label", className: "pointer", onClick: this.sendUploadSegmentEvent }, "Upload"),
                this.state.isFileTooLarge && (React.createElement("div", { className: "red pv2 fw5 f6", id: "file-size-error" }, "Sorry, your audio file was too large!")),
                React.createElement("input", { type: "file", id: "file-input", accept: "audio/*", onChange: this.handleFileInputChange })));
        };
        this.renderPreviewingState = () => {
            return (React.createElement("div", { className: "card pv3 recording-card" },
                React.createElement(PreviewAudio, { urlDownloadLink: this.state.urlDownloadLink, fileNameWithoutExtension: `${this.fileName}-${AudioTrimmerCutter.OutputFilenameSuffix}`, interactive: true, awsUploadInfo: this.props.awsUploadInfo, warnBeforeDeleting: this.warnBeforeDeleting, mp3Helper: this.mp3Helper, segmentAudioToolName: "Audio Trimmer Cutter", downloadCallback: this.onDownloadClick, onUploadComplete: this.handleTranscribeComplete })));
        };
        this.renderDeletingState = () => {
            return (React.createElement("div", { className: "card" },
                React.createElement("div", { className: "delete-confirmation-text" }, "Are you sure you want to delete?"),
                React.createElement("div", { className: "delete-confirmation-buttons" },
                    React.createElement("button", { id: "delete-button", type: "button", onClick: this.deleteRecording }, "Delete"),
                    React.createElement("button", { id: "not-sure-button", type: "button", onClick: this.returnToPreview }, "Not sure"))));
        };
        this.state = {
            toolState: ToolState.Ready,
            urlDownloadLink: '',
            isFileTooLarge: false,
        };
        this.fileName = null;
    }
    componentDidMount() {
        this.initAudioContext();
    }
    componentWillUnmount() {
        this.removeUnloadWarning();
        this.disconnectContext();
    }
    render() {
        const showSignUpGate = !this.props.isAuthenticated &&
            DownloadTrackerHelper.getDownloadCount(AudioTrimmerCutter.DownloadTrackerKey) >=
                AudioTrimmerCutter.UnauthenticatedDownloadLimit;
        return (React.createElement("div", { id: "audio-trimmer-cutter-container" },
            this.state.toolState === ToolState.Ready && showSignUpGate && this.renderSignUpGateState(),
            this.state.toolState === ToolState.Ready && !showSignUpGate && this.renderReadyState(),
            this.state.toolState === ToolState.Previewing && this.renderPreviewingState(),
            this.state.toolState === ToolState.Deleting && this.renderDeletingState()));
    }
}
AudioTrimmerCutter.OutputFilenameSuffix = '[Trimmed by Rev.com]';
AudioTrimmerCutter.DownloadTrackerKey = 'RevAudioTrimmerUnauthenticatedDownloadCount';
// Number of downloads an unauthenticated user gets before the sign-up gate appears.
AudioTrimmerCutter.UnauthenticatedDownloadLimit = 20;
AudioTrimmerCutter.MaxFileSize = 100000000; // ~100Mb
